import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@components/Loading';
import { LinkProjectModal } from '@components/LinkProjectModal';
import { useAuth } from '@hooks/useAuth';
import { projectService, type Project } from '@services/projectService';
import { modernizationService, type ModernizationProjectSummary } from '@services/modernizationService';

/**
 * Projects page - Main project management interface
 */
export function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [modernizationProjects, setModernizationProjects] = useState<ModernizationProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'completed' | 'archived'>('all');

  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingModernizationProject, setLinkingModernizationProject] = useState<{
    id: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  // Poll for analyzing projects
  useEffect(() => {
    const analyzingProjects = projects.filter(p => p.analysis_status === 'analyzing');

    if (analyzingProjects.length === 0) return;

    const pollInterval = setInterval(async () => {
      for (const project of analyzingProjects) {
        try {
          const analysisResults = project.analysis_results as any;
          const jobId = analysisResults?.graph_job_id;

          if (!jobId) {
            console.warn(`No job_id found for project ${project.id}`);
            continue;
          }

          const jobStatus = await projectService.checkJobStatus(jobId);

          if (jobStatus.status === 'completed') {
            await projectService.updateAnalysisStatus(
              project.id,
              'completed',
              {
                total_nodes: jobStatus.progress?.nodes_created || 0,
                total_relationships: jobStatus.progress?.relationships_created || 0,
                languages: jobStatus.progress?.languages_detected || [],
                total_files: jobStatus.progress?.files_total || 0,
              }
            );
            await loadProjects();
          } else if (jobStatus.status === 'failed') {
            await projectService.updateAnalysisStatus(
              project.id,
              'failed',
              undefined,
              jobStatus.error_message
            );
            await loadProjects();
          }
        } catch (err) {
          console.error(`Error polling job for project ${project.id}:`, err);
        }
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [projects]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load both analysis projects and modernization projects in parallel
      const [analysisResponse, modernizationResponse] = await Promise.all([
        projectService.getProjects(),
        modernizationService.getProjects('build_greenfield,build_modernization'),
      ]);

      setProjects(analysisResponse.projects);
      setModernizationProjects(modernizationResponse.projects);
    } catch (err: any) {
      console.error('Failed to load projects:', err);
      setError(err.response?.data?.detail || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartAnalysis = (projectType: 'analyze' | 'modernize' | 'migrate') => {
    if (projectType === 'analyze') {
      // Navigate to New Project page for code analysis (folder/git repo input)
      navigate('/workspace/code-graph/new');
    } else if (projectType === 'modernize') {
      // Navigate to Build New Software page
      navigate('/workspace/build-software');
    } else if (projectType === 'migrate') {
      // Navigate to Database Migration projects page
      navigate('/workspace/db-modernization/projects');
    }
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/workspace/code-graph/${projectId}`);
  };

  const handleContinueAnalysis = async (projectId: string) => {
    try {
      await projectService.startAnalysis(projectId);
      await loadProjects();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start analysis');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectService.deleteProject(projectId);
      await loadProjects();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleContinueModernization = (projectId: string) => {
    navigate(`/workspace/build-software?projectId=${projectId}`);
  };

  const handleViewPlan = (projectId: string) => {
    // Navigate to plan view (to be implemented)
    navigate(`/workspace/build-software?projectId=${projectId}&step=6`);
  };

  const handleViewAdrs = (projectId: string) => {
    // Navigate to ADRs view (to be implemented)
    navigate(`/workspace/build-software?projectId=${projectId}&step=7`);
  };

  const handleOpenLinkModal = (modernizationProjectId: string, modernizationProjectName: string) => {
    setLinkingModernizationProject({ id: modernizationProjectId, name: modernizationProjectName });
    setShowLinkModal(true);
  };

  const handleLinkToAnalysisProject = async (analysisProjectId: string) => {
    if (!linkingModernizationProject) return;

    try {
      await modernizationService.linkToAnalysisProject(
        linkingModernizationProject.id,
        analysisProjectId
      );
      await loadProjects(); // Reload to show updated link
      alert('Projects linked successfully!');
    } catch (err: any) {
      console.error('Failed to link projects:', err);
      throw err; // Re-throw so modal can handle it
    }
  };

  const handleCreateModernizationFromAnalysis = async (analysisProjectId: string, analysisProjectName: string) => {
    try {
      const newProject = await modernizationService.createProject({
        name: `Modernization: ${analysisProjectName}`,
        description: `Modernization project created from analysis project: ${analysisProjectName}`,
        project_type: 'build_modernization',
        linked_analysis_project_id: analysisProjectId,
        current_step: 1,
      });

      await loadProjects();

      // Navigate to the new project
      navigate(`/workspace/build-software?projectId=${newProject.id}`);
    } catch (err: any) {
      console.error('Failed to create modernization project:', err);
      alert(err.response?.data?.detail || 'Failed to create modernization project');
    }
  };

  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'active') return ['analyzing', 'created'].includes(project.analysis_status);
    if (activeFilter === 'completed') return ['analyzed', 'completed'].includes(project.analysis_status);
    if (activeFilter === 'archived') return project.analysis_status === 'failed';
    return true;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'analyzing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 In Progress</span>;
      case 'analyzed':
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">✅ Complete</span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">❌ Failed</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">⏳ Created</span>;
    }
  };

  const getProgressPercentage = (project: Project) => {
    if (project.analysis_status === 'completed' || project.analysis_status === 'analyzed') return 100;
    if (project.analysis_status === 'analyzing') return 65;
    if (project.analysis_status === 'failed') return 0;
    return 0;
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>RenovX</span>
        <span>&gt;</span>
        <span className="text-gray-900 font-medium">Projects</span>
      </div>

      {/* Start New Project Section */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Start New Project</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Analyze Existing Code Card */}
            <div className="border border-gray-200 rounded-lg p-6 hover:border-red-300 hover:shadow-lg transition-all flex flex-col">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Analyze Existing Code</h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow">
                Reverse engineer legacy apps to generate docs, architecture, and tech debt analysis
              </p>
              <button
                onClick={() => handleStartAnalysis('analyze')}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all mt-auto"
              >
                + New
              </button>
            </div>

            {/* Modernize Application Card */}
            <div className="border border-gray-200 rounded-lg p-6 hover:border-red-300 hover:shadow-lg transition-all flex flex-col">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Modernize Application</h3>
              <div className="flex-grow">
                <p className="text-sm text-gray-600 mb-1">
                  Build new or modernize using cloud-native architecture
                </p>
                <ul className="text-xs text-gray-500 mb-4 space-y-1">
                  <li>• From scratch</li>
                  <li>• From analysis</li>
                  <li>• Microservices</li>
                  <li>• Event-driven</li>
                </ul>
              </div>
              <button
                onClick={() => handleStartAnalysis('modernize')}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all mt-auto"
              >
                + New
              </button>
            </div>

            {/* Migrate DB Card */}
            <div className="border border-gray-200 rounded-lg p-6 hover:border-red-300 hover:shadow-lg transition-all flex flex-col">
              <div className="text-4xl mb-4">🗄️</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Migrate DB</h3>
              <div className="flex-grow">
                <p className="text-sm text-gray-600 mb-1">
                  Convert legacy databases to modern platforms
                </p>
                <ul className="text-xs text-gray-500 mb-4 space-y-1">
                  <li>• MSSQL → MySQL</li>
                  <li>• Oracle → PgSQL</li>
                  <li>• Schema + Data</li>
                  <li>• Automated conversion</li>
                </ul>
              </div>
              <button
                onClick={() => handleStartAnalysis('migrate')}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all mt-auto"
              >
                + New
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Your Projects Section */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Your Projects</h2>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeFilter === 'all'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveFilter('active')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeFilter === 'active'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              Active
            </button>
            <button
              onClick={() => setActiveFilter('completed')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeFilter === 'completed'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              Completed
            </button>
            <button
              onClick={() => setActiveFilter('archived')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeFilter === 'archived'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              Archived
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-all">
              🔍 Filter
            </button>
            <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-all">
              📊 View: Grid
            </button>
          </div>
        </div>

        {/* Projects List */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loading size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-red-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading projects</h3>
              <p className="mt-1 text-sm text-gray-600">{error}</p>
              <div className="mt-6">
                <button
                  onClick={loadProjects}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
              <p className="mt-1 text-sm text-gray-600">
                Get started by creating your first project above.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Analysis Projects Group */}
              <div className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900">🔍 Analysis Projects ({filteredProjects.length})</h3>
                </div>
                <div className="p-4 space-y-4">
                  {filteredProjects.map((project) => {
                    const progress = getProgressPercentage(project);
                    const analysisResults = project.analysis_results || {};
                    const createdDate = new Date(project.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    });

                    return (
                      <div key={project.id} className="border border-gray-200 rounded-lg p-4 hover:border-red-300 hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-lg font-bold text-gray-900">📦 {project.name}</h4>
                              {getStatusBadge(project.analysis_status)}
                            </div>
                            {project.description && (
                              <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                            )}
                          </div>
                          {progress > 0 && (
                            <span className="text-sm font-medium text-gray-700">{progress}%</span>
                          )}
                        </div>

                        {/* Progress bar */}
                        {progress > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-3">
                            <div
                              className="bg-red-600 h-1.5 rounded-full transition-all"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}

                        {/* Project metadata */}
                        <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                          <span>Type: Analysis</span>
                          <span>•</span>
                          <span>Started: {createdDate}</span>
                          <span>•</span>
                          <span>Status: {project.analysis_status}</span>
                        </div>

                        {/* Analysis results */}
                        {analysisResults.total_nodes && (
                          <div className="text-sm text-gray-700 mb-3">
                            <p>
                              Generated: Architecture ✓
                              {analysisResults.total_nodes && ` (${analysisResults.total_nodes} nodes)`}
                              {analysisResults.languages_detected && ` • Languages: ${analysisResults.languages_detected.join(', ')}`}
                            </p>
                          </div>
                        )}

                        {/* Source info */}
                        <div className="text-xs text-gray-500 mb-4">
                          <span className="font-medium">Source:</span> {project.source_type} - {project.source_location}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewProject(project.id)}
                            className="px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
                          >
                            View Details
                          </button>
                          {project.analysis_status === 'created' && (
                            <button
                              onClick={() => handleContinueAnalysis(project.id)}
                              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-all"
                            >
                              Continue Analysis
                            </button>
                          )}
                          {(project.analysis_status === 'analyzed' || project.analysis_status === 'completed') && (
                            <button
                              onClick={() => handleCreateModernizationFromAnalysis(project.id, project.name)}
                              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-300 hover:bg-green-100 rounded-lg transition-all inline-flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Create Modernization
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-300 hover:bg-red-50 rounded-lg transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Modernization Projects Section */}
              <div className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900">🔄 Modernization Projects ({modernizationProjects.length})</h3>
                </div>
                <div className="p-4 space-y-4">
                  {modernizationProjects.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      No modernization projects yet. Click "Modernize Application" above to get started.
                    </div>
                  ) : (
                    modernizationProjects.map((project) => {
                      const statusBadges = {
                        planning: { label: '🔵 Planning', color: 'blue' },
                        in_progress: { label: '🟡 In Progress', color: 'yellow' },
                        review: { label: '🟠 Review', color: 'orange' },
                        completed: { label: '✅ Complete', color: 'green' },
                        failed: { label: '❌ Failed', color: 'red' },
                      } as const;

                      const statusBadge = statusBadges[
                        (project.modernization_status as keyof typeof statusBadges) || 'planning'
                      ] ?? statusBadges.planning;

                      const createdDate = new Date(project.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      });

                      return (
                        <div key={project.id} className="border border-gray-200 rounded-lg p-4 hover:border-red-300 hover:shadow-md transition-all">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-lg font-bold text-gray-900">💻 {project.name}</h4>
                                <span className={`text-xs px-2 py-1 rounded bg-${statusBadge.color}-100 text-${statusBadge.color}-800`}>
                                  {statusBadge.label}
                                </span>
                              </div>
                              {project.description && (
                                <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-700">{project.progress_percentage || 0}%</span>
                          </div>

                          {/* Progress bar */}
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-3">
                            <div
                              className="bg-red-600 h-1.5 rounded-full transition-all"
                              style={{ width: `${project.progress_percentage || 0}%` }}
                            />
                          </div>

                          {/* Linked Analysis Project */}
                          {project.linked_analysis_project_name && (
                            <div className="text-sm text-gray-700 mb-2">
                              🔗 Source: <span className="font-medium">{project.linked_analysis_project_name}</span>
                            </div>
                          )}

                          {/* Strategy */}
                          {(project.architecture_style || project.cloud_platform) && (
                            <div className="text-sm text-gray-700 mb-2">
                              Strategy: {project.architecture_style || 'Not set'}, {project.cloud_platform || 'Not set'}
                            </div>
                          )}

                          {/* Phase Progress */}
                          {project.total_phases && (
                            <div className="text-sm text-gray-700 mb-3">
                              Phase {project.phase || 0}/{project.total_phases}
                            </div>
                          )}

                          {/* Current Step */}
                          {project.current_step && (
                            <div className="text-xs text-gray-500 mb-4">
                              Current: Step {project.current_step} of 9
                            </div>
                          )}

                          {/* Created Date */}
                          <div className="text-xs text-gray-500 mb-4">
                            Created {createdDate}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => handleContinueModernization(project.id)}
                              className="px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
                            >
                              Continue Development
                            </button>
                            {!project.linked_analysis_project_id && (
                              <button
                                onClick={() => handleOpenLinkModal(project.id, project.name)}
                                className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 hover:bg-blue-100 rounded-lg transition-all inline-flex items-center"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                  />
                                </svg>
                                Link to Analysis Project
                              </button>
                            )}
                            {project.current_step && project.current_step >= 6 && (
                              <button
                                onClick={() => handleViewPlan(project.id)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-all"
                              >
                                View Plan
                              </button>
                            )}
                            {project.current_step && project.current_step >= 7 && (
                              <button
                                onClick={() => handleViewAdrs(project.id)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-all"
                              >
                                View ADRs
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900">🗄️ Database Migration Projects (0)</h3>
                </div>
                <div className="p-8 text-center text-gray-500 text-sm">
                  No database migration projects yet. Click "Migrate DB" above to get started.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Link Project Modal */}
      <LinkProjectModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setLinkingModernizationProject(null);
        }}
        onLink={handleLinkToAnalysisProject}
        availableProjects={projects.filter(p => p.analysis_status === 'analyzed' || p.analysis_status === 'completed')}
        modernizationProjectName={linkingModernizationProject?.name || ''}
      />
    </div>
  );
}
